import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import { ReactComponent as LoadingIcon } from '@pkg/assets/icons/v2/loading-bars.svg';
import { Pagination } from '@pkg/components/Sections/Groups/GroupGrid/styles';
import { Button } from '@pkg/components/Sections/Profile/Buttons';

import { type GroupInvite } from '@app/client/common/structs/groups/groups';
import { type GroupMember } from '@app/gen';

import './MembersList.scss';
import { Member } from './components';

export interface MembersListProps {
    isOwner?: boolean;
    pendingGroupInvites?: GroupInvite[];
    members: GroupMember[];
    isLoading: boolean;
    hasNextPage: boolean;
    isFetching: boolean;
    listHeader: string;
    fetchNextPage: () => void;
}

export function MembersList({
    isOwner,
    fetchNextPage,
    pendingGroupInvites,
    hasNextPage,
    isFetching,
    listHeader,
    isLoading,
    members,
}: MembersListProps) {
    const { t: translate } = useTranslation(['common', 'groups']);

    const { ref, inView } = useInView();

    useEffect(() => {
        if (inView && !isFetching && hasNextPage) {
            fetchNextPage();
        }
    }, [inView, fetchNextPage, isFetching, hasNextPage]);

    return (
        <div className="yat-group-members__counts-and-members">
            <h3 className="yat-group-members__counts">{listHeader}</h3>

            {isLoading && (
                <div className="yat-group-members__loader" aria-label={translate('common:loading')}>
                    <LoadingIcon aria-hidden="true" />
                </div>
            )}

            {members?.map(
                (member) =>
                    member.primary_yat && (
                        <Member
                            memberId={member.id}
                            primaryYat={member.primary_yat}
                            twitter={member.twitter}
                            isOwner={isOwner}
                            permission={member.permission}
                            key={member.id}
                        />
                    ),
            )}

            {isOwner &&
                pendingGroupInvites?.map((pendingInvite) => {
                    const { id, invitee } = pendingInvite;
                    return (
                        <Member
                            memberId={invitee.id}
                            primaryYat={invitee.yat}
                            twitter={invitee.twitter_name}
                            isOwner={isOwner}
                            permission="Invitee"
                            key={invitee.id}
                            inviteId={id}
                        />
                    );
                })}

            {hasNextPage && !isFetching ? (
                <Pagination>
                    <Button ref={ref} disabled={isFetching} loading={isFetching} onClick={() => fetchNextPage()}>
                        {translate('groups:groups_btn_load_more')}
                    </Button>
                </Pagination>
            ) : null}
        </div>
    );
}
